<template>
	<div class="gxzz-product-page" :class="typeContainer" @click.capture="handleProductNavigationClick">
		<div class="top-head">
			<van-icon name="arrow-left" size="1.5rem" color="#86909c" @click="goBack" />
			<template v-if="pageFrom === 'classify'">
				<div class="title-row">{{ titles }}</div>
			</template>
			<template v-else>
				<SearchFake
					:firstcategory="firstcategory"
					:secondcategory="secondcategory"
					:thirdcategory="thirdcategory"
					:productnature="productnature"
					:titles="titles"
					:placeholders="placeholders"
					:companyId="companyId"
				/>
			</template>

			<span class="view-switch">
				<i class="icon-v2_liebiao iconfont" @click="toggleTo('list')" v-if="viewType === 'grid'"></i>
				<i class="icon-v2_jiugongge iconfont" @click="toggleTo('grid')" v-if="viewType === 'list'"></i>
			</span>
		</div>

		<div>
			<SortBar @sort="handleSortClick" :mainTitle="mainTitle" :companyId="companyId">
				<template #icon>
					<a rel="nofollow" href="javascript:void(0)" @click.prevent="showDrawer">
						<i class="iconfont icon-gongneng_shaixuan" :class="{ active: activeFilter }"></i>
					</a>
				</template>
			</SortBar>
		</div>

		<div v-if="showSkipSitePlaceholder && skipSiteProductCount === 0" class="skip-site-placeholder">
			<img class="skip-site-image" src="/staticSeo/images/nothing-space.png" alt="未找到相关商品" />
			<div class="skip-site-desc">暂无搜索结果，为您推荐其他站点的搜索结果</div>
		</div>

		<PaginationList
			v-else
			ref="paginationListRef"
			:fetch-data="fetchProductList"
			:initial-list="ssrListRes?.data?.list ?? []"
			:page-size="PAGE_SIZE"
			:margin-bottom="0"
			enableRefresh
			:scroll-container-style="{ height: 'calc(100vh - 7rem)' }"
		>
			<template #list="{ list: productList }">
				<div v-if="showSkipSitePlaceholder" class="skip-site-placeholder">
					<img class="skip-site-image" src="/staticSeo/images/nothing-space.png" alt="未找到相关商品" />
					<div class="skip-site-desc">暂无搜索结果，为您推荐其他站点的搜索结果</div>
				</div>
				<SmallImg showCart v-if="viewType === 'list'" :dataList="productList" :imgWidth="100" :imgHeight="100" />
				<BigImg showCart :showSite="false" v-if="viewType === 'grid'" :dataList="productList" />
			</template>
		</PaginationList>

		<GxzzFilterDrawer
			:show="showPopup"
			:filters="appliedFilters"
			:brand-options="filterOptions.brandList"
			:category-options="filterOptions.categoryOptions"
			:department-options="filterOptions.departmentList"
			:attr-options="filterOptions.commonAttrList"
			@update:show="handleDrawerVisibleChange"
			@close="handleDrawerClose"
			@confirm="handleFilterConfirm"
		/>

		<SubFoot v-if="footerType === 'child'" />
	</div>
</template>

<script setup>
import { cloneDeep } from 'lodash-es';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { showToast } from 'vant';
import { getDiscountList } from '@/api/goods.js';
import { getGxzzSearchProductList, getUserSite, queryWebsiteByCompanyId } from '@/api/index.js';
import PaginationList from '@/components/core/PaginationList.vue';
import BigImg from '@/components/BigImg.vue';
import SmallImg from '@/components/SmallImg.vue';
import SubFoot from '@/components/SubFoot.vue';
import SortBar from '@/pages/productList/components/SortBar.vue';
import { navigateBack } from '@/utils/utils';
import { getStorageSync, setStorageSync } from '@/utils/storage.js';
import { useAnalytics } from '@/composables/useAnalytics';
import { GXZZ_TRACKING_CHANNEL, setGxzzProductTrackingContextByHref } from '@/utils/gxzzTracking';
import GxzzFilterDrawer from './GxzzFilterDrawer.vue';

const PAGE_SIZE = 10;

const createEmptyFilters = () => ({
	brandIds: [],
	departmentIds: [],
	attrNames: [],
	category: {
		rootId: null,
		selectedId: null,
		secondId: null,
		thirdId: null,
		highlightChildIds: [],
	},
});

const hasAppliedFilters = filters =>
	!!(
		filters?.brandIds?.length ||
		filters?.departmentIds?.length ||
		filters?.attrNames?.length ||
		filters?.category?.selectedId
	);

const props = defineProps({
	initialCompanyId: {
		type: [String, Number],
		default: '',
	},
	websiteInfo: {
		type: Object,
		default: () => ({}),
	},
});

const route = useRoute();
const { trackEvent } = useAnalytics();

const footerType = ref('main');
const viewType = ref('list');
const showPopup = ref(false);
const pageFrom = ref('');
const titles = ref('');
const placeholders = ref('');
const isLogin = ref(false);
const productnature = ref(null);
const firstcategory = ref(null);
const secondcategory = ref(null);
const thirdcategory = ref(null);
const hotWordsId = ref('');
const companyId = ref('');
const companyCode = ref('');
const currentWebsiteInfo = ref({});
const topSortBarParams = ref({});
const appliedFilters = ref(createEmptyFilters());
const paginationListRef = ref(null);
const skipSiteFilter = ref(false);
const skipSiteProductCount = ref(0);
/** 当前搜索条件已上报的唯一标识，避免排序和筛选重复上报。 */
const searchTrackKey = ref('');

const filterOptions = reactive({
	brandList: [],
	categoryOptions: [],
	departmentList: [],
	commonAttrList: [],
});

const typeContainer = computed(() => (footerType.value === 'main' ? 'main-site' : 'child-site'));
const activeFilter = computed(() => hasAppliedFilters(appliedFilters.value));
const hasSortOrFilter = computed(
	() => activeFilter.value || (topSortBarParams.value.sortField && topSortBarParams.value.sortField !== 'default')
);
const mainTitle = computed(() => (hasSortOrFilter.value ? '综合排序' : '推荐排序'));
const showSkipSitePlaceholder = computed(() => skipSiteFilter.value);

/**
 * 上报国械智造子站搜索，sourceSign 由 useAnalytics 按系统来源自动补齐。
 * @param {number|string|null|undefined} total 搜索接口返回的结果总数
 */
const reportGxzzSearchQuery = total => {
	const keyword = titles.value.trim();
	if (!keyword) return;

	const currentTrackKey = `${keyword}|${route.query.searchType ?? ''}|${hotWordsId.value ?? ''}`;
	if (searchTrackKey.value === currentTrackKey) return;

	trackEvent({
		module: '搜索',
		eventCode: 'search_query',
		eventName: '搜索',
		attrName: 'channel_type',
		attrCode: '子站搜索框',
		ext1: keyword,
		ext3: Number(total) || 0,
		ext4: keyword,
		companyCode: companyCode.value,
	});
	searchTrackKey.value = currentTrackKey;
};

watch(
	() => props.websiteInfo,
	value => {
		if (value && Object.keys(value).length) {
			currentWebsiteInfo.value = { ...value };
		}
	},
	{ immediate: true, deep: true }
);

const getCurrentSiteInfo = async () => {
	if (currentWebsiteInfo.value?.companyCode) return currentWebsiteInfo.value;

	if (!companyId.value) return currentWebsiteInfo.value;

	try {
		const websiteRes = await queryWebsiteByCompanyId(companyId.value);
		if (String(websiteRes?.code) === '200' && websiteRes?.data) {
			currentWebsiteInfo.value = {
				...currentWebsiteInfo.value,
				...websiteRes.data,
			};
		}
	} catch (error) {
		console.warn('[gxzz] queryWebsiteByCompanyId failed', error);
	}

	if (!currentWebsiteInfo.value?.companyCode && import.meta.client) {
		const storedCompanyCode = getStorageSync('searchCompanyCode');
		if (!storedCompanyCode) return currentWebsiteInfo.value;

		currentWebsiteInfo.value = {
			...currentWebsiteInfo.value,
			companyCode: String(storedCompanyCode),
		};
	}

	return currentWebsiteInfo.value;
};

const initFromRoute = () => {
	const params = route.query;

	if (import.meta.server) {
		const cmdcAccessToken = useCookie('cmdc_access_token');
		isLogin.value = !!cmdcAccessToken.value;
	}

	companyId.value = props.initialCompanyId !== '' && props.initialCompanyId != null ? String(props.initialCompanyId) : String(params.companyId || '1');
	footerType.value = companyId.value && companyId.value !== '1' ? 'child' : 'main';

	titles.value = String(params.keyword || params.searchName || '').trim();
	placeholders.value = params.placeholders ? String(params.placeholders) : '产品名称/型号规格/厂家信息/商品描述';
	productnature.value = params.productnature || null;
	firstcategory.value = params.firstcategory || null;
	secondcategory.value = params.secondcategory || null;
	thirdcategory.value = params.thirdcategory || null;
	pageFrom.value = params.pageFrom || '';
	hotWordsId.value = params.hotWordsId || '';

	const initialCategoryId = thirdcategory.value || secondcategory.value || firstcategory.value || null;
	if (initialCategoryId) {
		appliedFilters.value.category.selectedId = initialCategoryId;
	}
};

const handleSortClick = async data => {
	const sortField = data.sortField;
	if (sortField === 'site' || sortField === 'onSaleTime') return;

	topSortBarParams.value = sortField === 'default' ? {} : { sortField, sort: data.sort || null };
	reloadList();
};

const toggleTo = type => {
	viewType.value = type;
};

const showDrawer = () => {
	showPopup.value = true;
};

const handleDrawerVisibleChange = value => {
	showPopup.value = value;
};

const handleDrawerClose = () => {
	showPopup.value = false;
};

const handleFilterConfirm = filters => {
	appliedFilters.value = cloneDeep(filters);
	showPopup.value = false;
	reloadList();
};

const goBack = () => {
	navigateBack();
};

/**
 * 记录国械智造子站商品进入详情页的来源。
 * @param {MouseEvent} event 页面点击事件
 */
const handleProductNavigationClick = event => {
	if (!(event.target instanceof Element)) return;
	// 区分子站全部商品与跨站推荐搜索结果。
	const isAllProductsPage = /\/products\/?$/.test(route.path);
	const isOtherSiteSearchResult = /\/search\/?$/.test(route.path) && skipSiteFilter.value;
	if (!isAllProductsPage && !isOtherSiteSearchResult) return;

	const productLink = event.target.closest('a[href]');
	// 跨站推荐使用搜索结果来源，其余沿用专区商品卡片来源。
	const trackingChannel = isOtherSiteSearchResult
		? GXZZ_TRACKING_CHANNEL.SUBSITE_SEARCH_RESULT
		: GXZZ_TRACKING_CHANNEL.SPECIAL_PRODUCT_CARD;
	setGxzzProductTrackingContextByHref(productLink?.getAttribute('href'), trackingChannel);
};

const reloadList = async () => {
	skipSiteFilter.value = false;
	skipSiteProductCount.value = 0;
	await nextTick();
	paginationListRef.value?.clearList?.();
	paginationListRef.value?.onRefresh?.();
};

const syncFilterOptions = data => {
	const brandList = Array.isArray(data?.brandList)
		? data.brandList
				.map(item => {
					const brandName =
						typeof item === 'string'
							? item.trim()
							: String(item?.localSubBrandName || item?.brandName || item?.name || '').trim();
					if (!brandName) return null;

					return {
						localSubBrandId: brandName,
						localSubBrandName: brandName,
					};
				})
				.filter(Boolean)
				.filter((item, index, list) => list.findIndex(option => option.localSubBrandName === item.localSubBrandName) === index)
		: [];
	const categoryOptions = Array.isArray(data?.categoryOptions) ? data.categoryOptions.filter(item => item?.categoryId && item?.categoryName) : [];
	const departmentList = Array.isArray(data?.departmentList) ? data.departmentList.filter(item => item?.departmentId && item?.departmentName) : [];
	const commonAttrList = Array.isArray(data?.commonAttrList)
		? data.commonAttrList
				.map(item => {
					const rawChildren = Array.isArray(item?.children)
						? item.children
								.map(child => ({
									...child,
									attrValue: String(child?.attrValue || '').trim(),
								}))
								.filter(child => child.attrValue)
						: [];
					const fallbackValues = (Array.isArray(item?.attrValue) ? item.attrValue : item?.attrValue ? [item.attrValue] : [])
						.map(value => String(value).trim())
						.filter(Boolean);
					const attrValues = Array.from(new Set((rawChildren.length ? rawChildren.map(child => child.attrValue) : fallbackValues).filter(Boolean)));
					const children = rawChildren.length
						? rawChildren.filter((child, index, list) => list.findIndex(option => option.attrValue === child.attrValue) === index)
						: attrValues.map(value => ({ attrValue: value }));
					const attrName = String(item?.attrName || attrValues[0] || '').trim();
					return {
						attrId: item?.attrId ?? null,
						attrName,
						children,
						attrValues,
					};
				})
				.filter(item => item.attrName && item.attrValues.length)
		: [];

	filterOptions.brandList = brandList;
	filterOptions.categoryOptions = categoryOptions;
	filterOptions.departmentList = departmentList;
	filterOptions.commonAttrList = commonAttrList;
	syncAppliedCategoryState();
};

const resolveCategorySelection = (categoryTree, selectedId) => {
	if (!selectedId) return null;

	for (const root of categoryTree || []) {
		if (String(root.categoryId) === String(selectedId)) {
			return {
				rootId: root.categoryId,
				secondId: null,
				thirdId: null,
				highlightChildIds: [],
			};
		}

		for (const second of root.children || []) {
			if (String(second.categoryId) === String(selectedId)) {
				return {
					rootId: root.categoryId,
					secondId: second.categoryId,
					thirdId: null,
					highlightChildIds: Array.isArray(second.children) ? second.children.map(item => item.categoryId) : [],
				};
			}

			for (const third of second.children || []) {
				if (String(third.categoryId) === String(selectedId)) {
					return {
						rootId: root.categoryId,
						secondId: second.categoryId,
						thirdId: third.categoryId,
						highlightChildIds: [third.categoryId],
					};
				}
			}
		}
	}

	return null;
};

const syncAppliedCategoryState = () => {
	const selectedId = appliedFilters.value.category.selectedId;
	if (!selectedId) return;

	const resolved = resolveCategorySelection(filterOptions.categoryOptions, selectedId);
	if (!resolved) return;

	appliedFilters.value.category = {
		...appliedFilters.value.category,
		...resolved,
	};
};

const buildRequestParams = async (pageNum, pageSize) => {
	const siteInfo = await getCurrentSiteInfo();
	companyCode.value = siteInfo?.companyCode || '';

	const params = {
		searchName: titles.value || '',
		needCompanyList: 0,
		pageNum,
		pageSize,
	};

	if (companyCode.value) {
		params.companyCodeList = [companyCode.value];
	}

	if (hotWordsId.value) {
		params.hotWordsId = hotWordsId.value;
	}

	if (appliedFilters.value.brandIds.length) {
		params.brandList = [...appliedFilters.value.brandIds];
	}

	if (appliedFilters.value.departmentIds.length) {
		params.departmentIds = [...appliedFilters.value.departmentIds];
	}

	if (appliedFilters.value.attrNames.length) {
		params.attrNames = [...appliedFilters.value.attrNames];
	}

	if (appliedFilters.value.category.selectedId) {
		params.subCategoryId = appliedFilters.value.category.selectedId;
	}

	if (topSortBarParams.value.sortField === 'sales') {
		params.salesVolumeOrder = 0;
	}

	if (topSortBarParams.value.sortField === 'price') {
		params.ranking = topSortBarParams.value.sort === 'ASC' ? '1' : '0';
	}

	return params;
};

const enrichProducts = async list => {
	const siteInfo = await getCurrentSiteInfo();
	const normalizedList = (Array.isArray(list) ? list : []).map(item => {
		const productId = item.productId || item.productid;
		const productCode = item.productCode || item.productcode;
		const itemCompanyId = item.companyId || item.companyid || item.mobileCompanyId || item.mobilecompanyid || companyId.value || siteInfo?.companyId || '';
		const filialeCode = item.filialeCode || item.filialecode || companyCode.value || siteInfo?.companyCode || '';
		const filialeName = item.filialeName || item.filialename || siteInfo?.companyName || siteInfo?.websiteName || '';

		return {
			...item,
			productId,
			productid: productId,
			productCode,
			productcode: productCode,
			companyId: itemCompanyId,
			companyName: item.companyName || item.companyname || siteInfo?.companyName || siteInfo?.websiteName || '',
			websiteName: item.websiteName || item.websitename || '',
			filialeCode,
			filialecode: filialeCode,
			filialeName,
			filialename: filialeName,
			defaultImageUrl: item.defaultImageUrl || item.defaultimageurl || item.imageUrl || '',
		};
	});

	const listProductIds = normalizedList
		.filter(item => item.productId && item.productCode && item.filialeCode)
		.map(item => ({
			productCode: item.productCode,
			productId: item.productId,
			companyCode: item.filialeCode,
		}));

	if (listProductIds.length) {
		const discountRes = await getDiscountList(listProductIds).catch(() => null);
		if (String(discountRes?.code) === '200') {
			normalizedList.forEach(product => {
				discountRes.data.forEach(discountItem => {
					if (product.productId === discountItem.productId) {
						product.zero = discountItem.zero;
						product.coupon = discountItem.coupon;
						if (discountItem.activity) {
							product.activityType = discountItem.activity.activityType;
						}
					}
				});
			});
		}
	}

	return normalizedList;
};

const requestProductListData = async (pageNum, pageSize) => {
	const reqParams = await buildRequestParams(pageNum, pageSize);
	const res = await getGxzzSearchProductList(reqParams);
	if (String(res?.code) !== '200') {
		throw new Error(res?.message || '获取列表失败');
	}

	return res.data || {};
};

const fetchProductListApi = async (pageNum, pageSize) => {
	const rawData = await requestProductListData(pageNum, pageSize);
	syncFilterOptions(rawData);
	const list = await enrichProducts(rawData.list || []);
	if (pageNum === 1) {
		skipSiteFilter.value = Number(rawData?.skipSiteFilter) === 1;
		skipSiteProductCount.value = list.length;
		if (import.meta.client) {
			reportGxzzSearchQuery(rawData?.total);
		}
	} else {
		skipSiteProductCount.value += list.length;
	}
	return list;
};

initFromRoute();

const { data: ssrListRes } = await useAsyncData(`gxzz-product-list-firstpage-${route.fullPath}`, async () => {
	try {
		const rawData = await requestProductListData(1, PAGE_SIZE);
		const list = await enrichProducts(rawData.list || []);
		return { code: 200, data: { list, filterData: rawData, total: rawData?.total ?? 0 } };
	} catch (error) {
		return { code: 500, message: error?.message || '请求失败', data: { list: [] } };
	}
});

syncFilterOptions(ssrListRes.value?.data?.filterData || {});
skipSiteFilter.value = Number(ssrListRes.value?.data?.filterData?.skipSiteFilter) === 1;
skipSiteProductCount.value = ssrListRes.value?.data?.list?.length || 0;

if (import.meta.client && String(ssrListRes.value?.code) !== '200') {
	showToast({ message: ssrListRes.value?.message || '请求失败', duration: 3000 });
}

const fetchProductList = async (pageNum, pageSize) => {
	if (import.meta.client) {
		return await fetchProductListApi(pageNum, pageSize);
	}
	return ssrListRes.value?.data?.list ?? [];
};

onMounted(async () => {
	isLogin.value = !!getStorageSync('cmdc_access_token');
	const siteInfo = await getCurrentSiteInfo();
	companyCode.value = siteInfo?.companyCode || '';
	reportGxzzSearchQuery(ssrListRes.value?.data?.total);

	if (import.meta.client) {
		setStorageSync('searchCompanyCode', companyCode.value || '');
	}

	if (isLogin.value && companyId.value !== '1') {
		await Promise.resolve(
			getUserSite({
				companyId: companyId.value,
				companyCode: companyCode.value,
			})
		).catch(() => null);
	}

	useWeixin().setShareInfo({
		title: '国械在线-商品搜索列表',
		desc: '我正在看【国械在线】商品搜索列表，分享给你啦。',
		img: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
	});
});
</script>

<style scoped lang="scss">
.gxzz-product-page {
	position: relative;
	width: 100%;
	height: 100%;
}

.top-head {
	display: flex;
	align-items: center;
	background-color: #fff;
	height: 3.67rem;
	padding: 0 12px;
	padding-top: 0.5rem;
}

.view-switch {
	margin-left: 8px;
}

.icon-gongneng_shaixuan {
	font-size: 17px !important;
	margin-left: 0.5rem;
	padding-right: 12px;

	&.active {
		color: #566adc;
	}
}

.skip-site-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 20px 12px;
	background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
	text-align: center;
}

.skip-site-image {
	width: 128px;
	height: 88px;
	object-fit: contain;
	margin-bottom: 8px;
}

.skip-site-desc {
	font-size: 12px;
	line-height: 17px;
	color: #8a95a8;
}

.title-row {
	display: flex;
	flex: 1;
	justify-content: center;
	font-size: 18px;
	font-weight: 500;
	align-items: center;
	color: #333333;
}

.gxzz-product-page :deep(.sort-key.site_field),
.gxzz-product-page :deep(.sort-key.onSaleTime_field) {
	display: none !important;
}
</style>
